import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Truck, Plus, Edit, Trash2, AlertTriangle, Search, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import LicensePlate from '@/components/LicensePlate';
import VehicleDocumentOCR from '@/components/VehicleDocumentOCR';
import VehicleDamageManager from '@/components/VehicleDamageManager';

const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    license_plate: '',
    model: '',
    tuv_expiry: '',
    last_oil_change: '',
    last_brake_check: '',
    current_km: 0,
    status: 'available'
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const res = await axios.get('/vehicles');
      setVehicles(res.data.vehicles || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        license_plate: vehicle.license_plate,
        model: vehicle.model,
        tuv_expiry: vehicle.tuv_expiry?.split('T')[0] || '',
        last_oil_change: vehicle.last_oil_change?.split('T')[0] || '',
        last_brake_check: vehicle.last_brake_check?.split('T')[0] || '',
        current_km: vehicle.current_km,
        status: vehicle.status
      });
    } else {
      setEditingVehicle(null);
      setFormData({
        license_plate: '',
        model: '',
        tuv_expiry: '',
        last_oil_change: '',
        last_brake_check: '',
        current_km: 0,
        status: 'available'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.license_plate || !formData.model || !formData.tuv_expiry) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      const payload = {
        ...formData,
        tuv_expiry: formData.tuv_expiry + 'T00:00:00',
        last_oil_change: formData.last_oil_change ? formData.last_oil_change + 'T00:00:00' : null,
        last_brake_check: formData.last_brake_check ? formData.last_brake_check + 'T00:00:00' : null,
        current_km: parseInt(formData.current_km)
      };

      if (editingVehicle) {
        await axios.put(`/vehicles/${editingVehicle.vehicle_id}`, payload);
        toast.success('Fahrzeug aktualisiert');
      } else {
        await axios.post('/vehicles', payload);
        toast.success('Fahrzeug erstellt');
      }

      setDialogOpen(false);
      loadVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Fahrzeug wirklich löschen?')) return;

    try {
      await axios.delete(`/vehicles/${vehicleId}`);
      toast.success('Fahrzeug gelöscht');
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredVehicles = vehicles.filter(v => 
    v.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTuvStatus = (tuvDate) => {
    const days = Math.ceil((new Date(tuvDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return { color: 'destructive', text: 'Abgelaufen', days };
    if (days < 30) return { color: 'destructive', text: `${days} Tage`, days };
    if (days < 90) return { color: 'secondary', text: `${days} Tage`, days };
    return { color: 'default', text: `${days} Tage`, days };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="vehicle-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Fahrzeugverwaltung
          </h1>
          <p className="text-zinc-400">Alle Fahrzeuge verwalten</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-primary hover:bg-primary/90" 
              onClick={() => handleOpenDialog()}
              data-testid="add-vehicle-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Fahrzeug hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingVehicle ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}
              </DialogTitle>
              <DialogDescription>
                {editingVehicle ? 'Fahrzeugdaten aktualisieren' : 'Neues Fahrzeug zur Flotte hinzufügen'}
              </DialogDescription>
            </DialogHeader>
            
            {/* OCR Component - nur bei neuem Fahrzeug */}
            {!editingVehicle && (
              <div className="mb-4">
                <VehicleDocumentOCR 
                  onDataExtracted={(data) => {
                    setFormData({
                      ...formData,
                      license_plate: data.license_plate || formData.license_plate,
                      model: data.model || formData.model,
                      tuv_expiry: data.tuv_expiry || formData.tuv_expiry,
                      current_km: data.current_km || formData.current_km
                    });
                    toast.success('Fahrzeugdaten automatisch ausgefüllt!');
                  }}
                />
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="license_plate">Kennzeichen *</Label>
                <Input
                  id="license_plate"
                  value={formData.license_plate}
                  onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                  placeholder="B-XY 123"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="license-plate-input"
                />
              </div>
              <div>
                <Label htmlFor="model">Modell *</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({...formData, model: e.target.value})}
                  placeholder="Mercedes Sprinter"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="model-input"
                />
              </div>
              <div>
                <Label htmlFor="tuv_expiry">TÜV Ablauf *</Label>
                <Input
                  id="tuv_expiry"
                  type="date"
                  value={formData.tuv_expiry}
                  onChange={(e) => setFormData({...formData, tuv_expiry: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="tuv-input"
                />
              </div>
              <div>
                <Label htmlFor="current_km">Kilometerstand *</Label>
                <Input
                  id="current_km"
                  type="number"
                  value={formData.current_km}
                  onChange={(e) => setFormData({...formData, current_km: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="km-input"
                />
              </div>
              <div>
                <Label htmlFor="last_oil_change">Letzter Ölwechsel</Label>
                <Input
                  id="last_oil_change"
                  type="date"
                  value={formData.last_oil_change}
                  onChange={(e) => setFormData({...formData, last_oil_change: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label htmlFor="last_brake_check">Letzte Bremsenprüfung</Label>
                <Input
                  id="last_brake_check"
                  type="date"
                  value={formData.last_brake_check}
                  onChange={(e) => setFormData({...formData, last_brake_check: e.target.value})}
                  className="bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="available">Verfügbar</SelectItem>
                    <SelectItem value="in_use">Im Einsatz</SelectItem>
                    <SelectItem value="maintenance">Wartung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSubmit} className="flex-1" data-testid="save-vehicle-btn">
                {editingVehicle ? 'Aktualisieren' : 'Erstellen'}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Abbrechen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
        <Input
          placeholder="Suche nach Kennzeichen oder Modell..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-zinc-900">
                  <TableHead className="text-zinc-400">Kennzeichen</TableHead>
                  <TableHead className="text-zinc-400">Modell</TableHead>
                  <TableHead className="text-zinc-400">Kilometerstand</TableHead>
                  <TableHead className="text-zinc-400">TÜV</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVehicles.map((vehicle) => {
                  const tuvStatus = getTuvStatus(vehicle.tuv_expiry);
                  
                  return (
                    <TableRow key={vehicle.vehicle_id} className="border-zinc-800 hover:bg-zinc-800" data-testid={`vehicle-row-${vehicle.vehicle_id}`}>
                      <TableCell>
                        <LicensePlate plate={vehicle.license_plate} size="md" />
                      </TableCell>
                      <TableCell className="text-zinc-300">{vehicle.model}</TableCell>
                      <TableCell className="font-mono text-zinc-300">{vehicle.current_km?.toLocaleString()} km</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant={tuvStatus.color} className="capitalize">
                            {tuvStatus.text}
                          </Badge>
                          {tuvStatus.days < 30 && (
                            <AlertTriangle className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={vehicle.status === 'in_use' ? 'default' : vehicle.status === 'maintenance' ? 'destructive' : 'secondary'}
                          className="capitalize"
                        >
                          {vehicle.status === 'in_use' ? 'Im Einsatz' : vehicle.status === 'maintenance' ? 'Wartung' : 'Verfügbar'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedVehicle(vehicle);
                              setDamageDialogOpen(true);
                            }}
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-950"
                            title="Schadensregister"
                          >
                            <FileWarning className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenDialog(vehicle)}
                            data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-950"
                            onClick={() => handleDelete(vehicle.vehicle_id)}
                            data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {filteredVehicles.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              {searchTerm ? 'Keine Fahrzeuge gefunden' : 'Noch keine Fahrzeuge vorhanden'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Damage Manager Dialog */}
      {selectedVehicle && (
        <VehicleDamageManager
          vehicleId={selectedVehicle.vehicle_id}
          licensePlate={selectedVehicle.license_plate}
          isOpen={damageDialogOpen}
          onClose={() => setDamageDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default VehicleManagement;
